import { useState, type FormEvent } from "react";
import { accessLinkReturnTo, isSubscriberNotFoundReason, requestAccessLink } from "../lib/accessLink";
import { NetworkingRoomPreview } from "./NetworkingRoomPreview";
import { UnknownEmailNotice } from "./UnknownEmailNotice";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cardClass =
  "rounded-xl border border-slate-800 bg-slate-900/60 shadow-xl backdrop-blur";

export const AttendeeSignIn = ({ compact = false }: { compact?: boolean }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unknownEmail, setUnknownEmail] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setUnknownEmail(false);
    setLinkSent(false);

    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSendingLink(true);
    try {
      const returnTo = accessLinkReturnTo(
        window.location.pathname,
        window.location.search,
      );
      const { ok, error: linkError, reason } = await requestAccessLink(trimmed, returnTo);
      if (!ok) {
        if (isSubscriberNotFoundReason(reason)) {
          setUnknownEmail(true);
          setError(null);
        } else {
          setError(linkError ?? "Could not send link. Try again.");
        }
        return;
      }
      setLinkSent(true);
    } finally {
      setSendingLink(false);
    }
  };

  const formBody = linkSent ? (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-100">
      <p className="font-medium">Check your inbox</p>
      <p className="mt-1 text-xs text-emerald-200/90">
        We sent a one-click sign-in link to{" "}
        <span className="font-medium text-emerald-50">{email.trim()}</span>. Open
        it on this device — the link returns you to{" "}
        <span className="font-medium text-emerald-50">{window.location.host}</span>.
      </p>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="space-y-3">
      <label className="block">
        <span className={compact ? "sr-only" : "text-sm font-medium text-slate-300"}>
          Email
        </span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className={`${
            compact ? "" : "mt-1"
          } w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30`}
        />
      </label>

      {unknownEmail ? (
        <UnknownEmailNotice compact={compact} />
      ) : error ? (
        <p
          role="alert"
          className={`rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 ${
            compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm"
          }`}
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={sendingLink}
        className={`w-full rounded-lg bg-emerald-500 font-medium text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60 ${
          compact ? "px-3 py-2 text-sm" : "px-4 py-2"
        }`}
      >
        {sendingLink ? "Sending…" : "Email me a sign-in link"}
      </button>
    </form>
  );

  if (compact) {
    return (
      <section
        id="sign-in"
        className={`${cardClass} flex h-full min-h-0 scroll-mt-6 flex-col p-4 md:p-5`}
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Already registered?
          </p>
          <p className="mt-2 text-xs leading-relaxed text-slate-400">
            We email you a one-click link — no password. Use the email you registered
            with.
          </p>
        </div>
        <div className="mt-auto flex flex-col gap-3 pt-4">
          {formBody}
          <p className="text-center text-[11px] leading-snug text-slate-500">
            Day-of access only. Guest list required after you open the link.
          </p>
          <NetworkingRoomPreview />
        </div>
      </section>
    );
  }

  return (
    <section id="sign-in" className="scroll-mt-6">
      <div className="mb-3 px-1 text-left">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Already registered?
        </p>
        <p className="mt-2 text-sm text-slate-400">
          Enter your email and we&apos;ll send a magic link to sign in on event day.
        </p>
      </div>
      <div className={`${cardClass} p-6`}>{formBody}</div>
      <p className="mt-3 text-center text-xs text-slate-500">
        Day-of access only. Your email must be on the guest list.
      </p>
    </section>
  );
};
